import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { formatRp, formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Search, Plus, MapPin, Calendar, Layers, BriefcaseBusiness, WalletCards, Activity, Gauge, SlidersHorizontal } from 'lucide-react';
import ProjectFormDialog from '@/components/ProjectFormDialog';
import { useAuth } from '@/context/AuthContext';
import { useBranding } from '@/context/BrandingContext';

export default function HomePage() {
  const { can } = useAuth();
  const canWrite = can('business.write');
  const { branding } = useBranding();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/projects', { params: { search: search || undefined, status: statusFilter } });
      setProjects(res.data);
    } catch (e) {
      toast.error('Gagal memuat daftar project');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const sorted = useMemo(() => {
    const arr = [...projects];
    if (sortBy === 'name') arr.sort((a,b) => (a.name||'').localeCompare(b.name||''));
    else if (sortBy === 'budget_desc') arr.sort((a,b) => (b.budget||0) - (a.budget||0));
    else if (sortBy === 'progress_desc') arr.sort((a,b) => (b.progress||0) - (a.progress||0));
    // newest is server default
    return arr;
  }, [projects, sortBy]);

  const portfolio = useMemo(() => projects.reduce((summary, project) => {
    summary.budget += Number(project.budget) || 0;
    summary.expense += Number(project.total_expense) || 0;
    summary.remaining += Number(project.sisa_budget) || 0;
    if (project.status === 'On Progress') summary.active += 1;
    return summary;
  }, { budget: 0, expense: 0, remaining: 0, active: 0 }), [projects]);

  return (
    <div className="animate-in-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{branding.application_name}</h1>
          <p className="text-sm text-muted-fpccs mt-1">{branding.tagline}</p>
        </div>
        {canWrite && <Button
          data-testid="btn-add-project"
          onClick={() => { setEditing(null); setOpenForm(true); }}
          className="bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)] font-medium"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Project Baru
        </Button>}
      </div>

      {!loading && projects.length > 0 && (
        <section aria-label="Ringkasan portfolio" className="mb-6 grid grid-cols-2 gap-3 xl:grid-cols-4">
          <PortfolioCard icon={BriefcaseBusiness} label="Total Project" value={String(projects.length)} hint={`${portfolio.active} sedang berjalan`} />
          <PortfolioCard icon={WalletCards} label="Total RAB" value={formatRp(portfolio.budget, { compact: true })} hint="Portfolio terfilter" />
          <PortfolioCard icon={Activity} label="Total Pengeluaran" value={formatRp(portfolio.expense, { compact: true })} hint="Akumulasi project" tone="warning" />
          <PortfolioCard icon={Gauge} label="Sisa Budget" value={formatRp(portfolio.remaining, { compact: true })} hint={portfolio.remaining < 0 ? 'Melebihi budget' : 'Masih tersedia'} tone={portfolio.remaining < 0 ? 'danger' : 'success'} />
        </section>
      )}

      {/* Toolbar */}
      <div className="card-fpccs p-3 sm:p-4 mb-6">
        <div className="flex items-center gap-2 sm:hidden">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fpccs-text-subtle)]" />
            <Input
              data-testid="project-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari..."
              className="pl-9 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]"
            />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" aria-label="Buka filter project" className="relative flex h-10 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--fpccs-border)] bg-[var(--fpccs-surface-2)] text-muted-fpccs transition hover:text-[var(--fpccs-primary)]">
                <SlidersHorizontal className="h-4 w-4" />
                {(statusFilter !== 'all' || sortBy !== 'newest') && <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-[var(--fpccs-primary)]" />}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[min(19rem,calc(100vw-2rem))] space-y-3 rounded-2xl border-[var(--fpccs-border)] bg-[var(--fpccs-glass-strong)] p-4 backdrop-blur-xl">
              <div><div className="mb-1.5 text-xs font-medium text-muted-fpccs">Status project</div><ProjectStatusSelect value={statusFilter} onChange={setStatusFilter} testid="project-status-filter-mobile" /></div>
              <div><div className="mb-1.5 text-xs font-medium text-muted-fpccs">Urutkan</div><ProjectSortSelect value={sortBy} onChange={setSortBy} testid="project-sort-mobile" /></div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="hidden gap-3 sm:flex">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fpccs-text-subtle)]" />
            <Input data-testid="project-search-input-desktop" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari project, client, lokasi..." className="pl-9 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]" />
          </div>
          <ProjectStatusSelect value={statusFilter} onChange={setStatusFilter} testid="project-status-filter" className="w-[180px]" />
          <ProjectSortSelect value={sortBy} onChange={setSortBy} testid="project-sort" className="w-[180px]" />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_,i) => <Skeleton key={i} className="h-56 rounded-2xl bg-[var(--fpccs-surface-1)]" />)}
        </div>
      ) : sorted.length === 0 ? (
        <div className="card-fpccs p-12 text-center">
          <Layers className="w-8 h-8 mx-auto text-[var(--fpccs-primary)] mb-2" />
          <div className="text-lg font-medium">Belum ada project</div>
          <div className="text-sm text-muted-fpccs mt-1">Buat project pertama untuk mulai mencatat pemasukan/pengeluaran.</div>
          {canWrite && <Button
            data-testid="btn-add-project-empty"
            onClick={() => { setEditing(null); setOpenForm(true); }}
            className="mt-4 bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)]"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Buat Project
          </Button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map((p) => <ProjectCard key={p.id} p={p} canWrite={canWrite} onEdit={() => { setEditing(p); setOpenForm(true); }} />)}
        </div>
      )}

      <ProjectFormDialog
        open={openForm}
        onOpenChange={setOpenForm}
        initial={editing}
        onSaved={load}
      />
    </div>
  );
}

function ProjectStatusSelect({ value, onChange, testid, className = 'w-full' }) {
  return <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testid} className={`${className} bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]`}><SelectValue placeholder="Semua Status" /></SelectTrigger><SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="Planning">Planning</SelectItem><SelectItem value="On Progress">On Progress</SelectItem><SelectItem value="On Hold">On Hold</SelectItem><SelectItem value="Selesai">Selesai</SelectItem><SelectItem value="Dibatalkan">Dibatalkan</SelectItem></SelectContent></Select>;
}

function ProjectSortSelect({ value, onChange, testid, className = 'w-full' }) {
  return <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testid} className={`${className} bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]`}><SelectValue placeholder="Urutkan" /></SelectTrigger><SelectContent><SelectItem value="newest">Terbaru</SelectItem><SelectItem value="name">Nama A-Z</SelectItem><SelectItem value="budget_desc">Budget Terbesar</SelectItem><SelectItem value="progress_desc">Progress Tertinggi</SelectItem></SelectContent></Select>;
}

function ProjectCard({ p, onEdit, canWrite }) {
  const pct = Math.max(0, Math.min(100, p.progress || 0));
  const budgetPct = p.budget > 0 ? Math.min(100, (p.total_expense / p.budget) * 100) : 0;
  return (
    <Link
      to={`/projects/${p.id}`}
      data-testid={`project-card-${p.id}`}
      className="group card-fpccs block p-5 transition-all duration-200 hover:-translate-y-1 hover:border-[rgba(var(--fpccs-primary-rgb),0.34)] hover:shadow-[0_22px_54px_rgba(var(--fpccs-primary-rgb),0.12)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-subtle-fpccs">{p.client_name || 'Tanpa Client'}</div>
          <div className="text-lg font-semibold truncate mt-0.5">{p.name}</div>
        </div>
        <StatusBadge status={p.status} />
      </div>

      <div className="mt-3 flex items-center gap-3 text-xs text-muted-fpccs">
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.location || '-'}</span>
        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(p.start_date)}</span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] text-subtle-fpccs uppercase tracking-wider">Budget</div>
          <div className="text-sm font-semibold font-mono">{formatRp(p.budget, { compact: true })}</div>
        </div>
        <div>
          <div className="text-[11px] text-subtle-fpccs uppercase tracking-wider">Sisa Budget</div>
          <div className={`text-sm font-semibold font-mono ${p.sisa_budget < 0 ? 'text-[var(--fpccs-danger)]' : 'text-[var(--fpccs-success)]'}`}>{formatRp(p.sisa_budget, { compact: true })}</div>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex justify-between text-[11px] text-subtle-fpccs mb-1">
          <span>Progress</span><span>{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-[var(--fpccs-surface-3)] overflow-hidden">
          <div className="h-full bg-[var(--fpccs-primary)]" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="mt-2">
        <div className="flex justify-between text-[11px] text-subtle-fpccs mb-1">
          <span>Budget Terpakai</span><span>{budgetPct.toFixed(0)}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-[var(--fpccs-surface-3)] overflow-hidden">
          <div className={`h-full ${budgetPct > 90 ? 'bg-[var(--fpccs-danger)]' : budgetPct > 70 ? 'bg-[var(--fpccs-warning)]' : 'bg-[var(--fpccs-success)]'}`} style={{ width: `${budgetPct}%` }} />
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-[var(--fpccs-border)] flex justify-between items-center text-xs">
        <span className="text-subtle-fpccs">Est. Selesai: {formatDate(p.end_date)}</span>
        {canWrite && <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(); }}
          data-testid={`btn-edit-project-${p.id}`}
          className="text-[var(--fpccs-primary)] hover:text-[var(--fpccs-primary-2)]"
        >Edit</button>}
      </div>
    </Link>
  );
}

function PortfolioCard({ icon: Icon, label, value, hint, tone = 'primary' }) {
  const tones = {
    primary: 'text-[var(--fpccs-primary)] bg-[rgba(var(--fpccs-primary-rgb),0.12)]',
    success: 'text-[var(--fpccs-success)] bg-emerald-500/10',
    warning: 'text-[var(--fpccs-warning)] bg-amber-500/10',
    danger: 'text-[var(--fpccs-danger)] bg-red-500/10',
  };
  return (
    <div className="card-fpccs flex min-w-0 items-center gap-3 p-4 sm:p-5">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-subtle-fpccs sm:text-[11px]">{label}</div>
        <div className="truncate font-mono text-base font-semibold sm:text-lg">{value}</div>
        <div className="truncate text-[10px] text-subtle-fpccs sm:text-xs">{hint}</div>
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    'Planning': 'bg-[rgba(74,163,223,0.14)] text-[#4AA3DF] border-[rgba(74,163,223,0.28)]',
    'On Progress': 'bg-[rgba(var(--fpccs-primary-rgb),0.14)] text-[var(--fpccs-primary)] border-[rgba(var(--fpccs-primary-rgb),0.28)]',
    'On Hold': 'bg-[rgba(224,184,74,0.14)] text-[#E0B84A] border-[rgba(224,184,74,0.28)]',
    'Selesai': 'bg-[rgba(47,191,113,0.14)] text-[#2FBF71] border-[rgba(47,191,113,0.28)]',
    'Dibatalkan': 'bg-[rgba(224,90,79,0.14)] text-[#E05A4F] border-[rgba(224,90,79,0.28)]',
  };
  const cls = map[status] || map['Planning'];
  return (
    <span className={`shrink-0 px-2 py-0.5 rounded-md text-[11px] border ${cls}`}>{status || 'Planning'}</span>
  );
}
